// pages/students/index.tsx
import {
  createStudentColumns,
  StudentActionType,
} from "@/colDef/studentColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import Search from "@/components/Search";
import {
  useDeleteStudentMutation,
  useStudentsQuery,
} from "@/hooks/api/student.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog } from "@/hooks/useDialogState";
import { usePagination } from "@/hooks/usePagination";
import { StudentParamsType, StudentType } from "@/services/student.service";
import { Filter, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

const StudentsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const { page, limit, setPage, resetPage } = usePagination({
    initialLimit: 10,
  });
  const debouncedSearch = useDebounce(searchTerm, 400);

  const deleteDialog = useAlertDialog();
  // TODO: Student creation from this page requires a batch selection — use StudentDialog from Batch View instead.

  const filters: StudentParamsType = useMemo(
    () => ({
      limit,
      page,
      ...(debouncedSearch && { search: debouncedSearch }),
    }),
    [debouncedSearch, page, limit]
  );

  const { data, isLoading, isError, refetch } = useStudentsQuery(filters);
  const deleteMutation = useDeleteStudentMutation(filters);

  const students = data?.students ?? [];
  const pagination = data?.metaData?.pagination;
  const totalEntries = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: StudentActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to remove{" "}
              <strong className="text-gray-800">{payload.name}</strong>? This
              action cannot be undone.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      }
      // Edit is handled from the Batch View page — not supported here
    },
    [deleteDialog, students]
  );

  const columns = useMemo(
    () => createStudentColumns(handleActionClick),
    [handleActionClick]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load students."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Student Management</h1>
        <div className="flex items-center gap-3">
          {/* Advanced Filters — placeholder for future implementation */}
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => {
              /* TODO: open advanced filters panel */
            }}
          >
            <Filter size={15} />
            Advanced Filters
          </Button>

          <Button
            className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
            onClick={() => {}}
            disabled
          >
            <Plus size={16} />
            Add New
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <Search
          placeholder="Search by name, roll no, dept..."
          value={searchTerm}
          onValueChange={(val) => {
            setSearchTerm(val);
            resetPage();
          }}
          className={{ inputGroupClass: "max-w-xs" }}
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<StudentType, any> columns={columns} data={students} />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={students.length}
        onPageChange={setPage}
      />

      {/* Create / Edit Dialog — To create students, navigate to a Batch View and use Add Student there */}

      {/* Delete Confirmation */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Remove"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default StudentsPage;
